import { type GetVisitsWithCaseDataResponse } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { logEvent } from "@src/appV2/lib/analytics";
import { getVisitQueryKey } from "@src/appV2/redesign/InHome/useGetVisitData";
import { getVisitsQueryKey } from "@src/appV2/redesign/InHome/useGetVisitsWithCaseData";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { IN_HOME_USER_EVENTS } from "../lib/events";
import {
  type LegacyVisitOccurrenceData,
  type LogVisitRequest,
  logVisitRequestSchema,
  logVisitResponseSchema,
} from "../types";

export const LOG_VISIT_URL = `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/visits/:visitId/occurrences`;

export function useLogVisit(
  visitId: string,
  options: UseMutationOptions<LegacyVisitOccurrenceData, string, LogVisitRequest> = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params) => {
      const response = await post({
        url: LOG_VISIT_URL.replace(":visitId", visitId),
        requestSchema: logVisitRequestSchema,
        responseSchema: logVisitResponseSchema,
        data: params,
      });
      return response.data.data;
    },
    onSuccess: (visitOccurrence) => {
      const visitQueries =
        queryClient.getQueriesData<GetVisitsWithCaseDataResponse>(getVisitsQueryKey);
      const visits = visitQueries.flatMap(([_, queryData]) => queryData?.data);
      const matchedVisit = visits.find(
        (visit) => isDefined(visit) && visit.id === visitOccurrence.attributes.visitId
      );

      logEvent(IN_HOME_USER_EVENTS.VISIT_LOGGED, {
        caseId: matchedVisit?.attributes.caseId,
        visitId: visitOccurrence.attributes.visitId,
        type: matchedVisit?.attributes.type,
        workerReq: matchedVisit?.attributes.workerReq,
        visitOccurrenceId: visitOccurrence.id,
      });

      if (isDefined(matchedVisit) && isDefined(matchedVisit.attributes.caseId)) {
        void queryClient.invalidateQueries(
          getVisitQueryKey(visitId, matchedVisit.attributes.caseId)
        );
      }

      void queryClient.invalidateQueries(getVisitsQueryKey);
    },
    ...options,
  });
}
